import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import OlMap from 'ol/Map';
import View from 'ol/View';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { defaults as defaultControls } from 'ol/control';
import { fromLonLat, transformExtent } from 'ol/proj';
import { Fill, Stroke, Style } from 'ol/style';
import 'ol/ol.css';
import { MapLayerManager } from '../shared/utils/MapLayerManager';
import { useTheme } from '../shared/theme/ThemeContext';
import { apiErrorMessage, createCarbonProject } from './api';

interface Candidate {
  id: string;
  region: string;
  penro: number | null;
  district: string;
  cenro: string;
  municipality: string;
  psgc: string;
  investment_type: string;
  form_id: string;
  investment_class: string;
  ppf: string;
  area_ha: number | null;
  bbox: [number, number, number, number];
  ring: [number, number][];
}

interface CandidateFile {
  count: number;
  display_note: string;
  candidates: Candidate[];
}

const QUICK_REGIONS = [
  { id: '', label: 'Philippines' },
  { id: 'XI', label: 'Region XI' },
  { id: 'VIII', label: 'Region VIII' },
  { id: 'XII', label: 'Region XII' },
  { id: 'VII', label: 'Region VII' },
  { id: 'CALABARZON', label: 'CALABARZON' },
];

const SHORTLIST_KEY = 'digisaka.sflma.shortlist';

function loadShortlist(): string[] {
  try {
    const raw = localStorage.getItem(SHORTLIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function formatHa(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return 'Unknown';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ha`;
}

function typeKey(value: string): string {
  return value.trim().toLowerCase();
}

export default function SflmaExplorer({ publicPreview = false }: { publicPreview?: boolean }) {
  const { isDark } = useTheme();
  const mapHost = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<OlMap | null>(null);
  const layerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const visibleRef = useRef<Set<string>>(new Set());
  const selectedRef = useRef<string | null>(null);

  const [file, setFile] = useState<CandidateFile | null>(null);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('');
  const [investment, setInvestment] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [shortlistOnly, setShortlistOnly] = useState(false);
  const [shortlist, setShortlist] = useState<string[]>(loadShortlist);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const url = `${import.meta.env.BASE_URL}sflma/candidates.json`;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Candidate file returned ${response.status}`);
        return response.json() as Promise<CandidateFile>;
      })
      .then((data) => {
        if (!cancelled) setFile(data);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Could not load candidates.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const investmentOptions = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const candidate of file?.candidates ?? []) {
      const key = typeKey(candidate.investment_type);
      const label = candidate.investment_type.trim() || 'Not recorded';
      const current = counts.get(key);
      if (!current) counts.set(key, { label, count: 1 });
      else current.count += 1;
    }
    return [...counts.entries()]
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [file]);

  const regions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const candidate of file?.candidates ?? []) {
      const name = candidate.region || 'Unknown';
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [file]);

  const filtered = useMemo(() => {
    const min = minArea.trim() === '' ? null : Number(minArea);
    const max = maxArea.trim() === '' ? null : Number(maxArea);
    const needle = query.trim().toLowerCase();
    const short = new Set(shortlist);
    return (file?.candidates ?? []).filter((candidate) => {
      if (region && candidate.region !== region) return false;
      if (investment && typeKey(candidate.investment_type) !== investment) return false;
      if (shortlistOnly && !short.has(candidate.id)) return false;
      if (min != null && !Number.isNaN(min) && (candidate.area_ha ?? -1) < min) return false;
      if (max != null && !Number.isNaN(max) && (candidate.area_ha ?? Number.POSITIVE_INFINITY) > max) return false;
      if (!needle) return true;
      const hay = [
        candidate.id,
        candidate.form_id,
        candidate.municipality,
        candidate.region,
        candidate.cenro,
        candidate.investment_type,
        candidate.investment_class,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [file, investment, maxArea, minArea, query, region, shortlist, shortlistOnly]);

  const grossArea = useMemo(
    () => filtered.reduce((sum, candidate) => sum + (candidate.area_ha ?? 0), 0),
    [filtered],
  );
  const selected = filtered.find((candidate) => candidate.id === selectedId)
    ?? file?.candidates.find((candidate) => candidate.id === selectedId)
    ?? null;

  useEffect(() => {
    visibleRef.current = new Set(filtered.map((candidate) => candidate.id));
    layerRef.current?.changed();
  }, [filtered]);

  useEffect(() => {
    selectedRef.current = selectedId;
    layerRef.current?.changed();
  }, [selectedId]);

  useEffect(() => {
    const host = mapHost.current;
    if (!host || mapRef.current || !file) return;
    const bases = MapLayerManager.createBaseLayers();
    const source = new VectorSource();
    const features = file.candidates.map((candidate) => {
      const ring = candidate.ring.map(([lon, lat]) => fromLonLat([lon, lat]));
      const feature = new Feature({ geometry: new Polygon([ring]) });
      feature.setId(candidate.id);
      feature.set('id', candidate.id);
      return feature;
    });
    source.addFeatures(features);
    const layer = new VectorLayer({
      source,
      style: (feature) => {
        const id = String(feature.get('id') ?? '');
        if (!visibleRef.current.has(id)) return undefined;
        const active = id === selectedRef.current;
        return new Style({
          fill: new Fill({ color: active ? 'rgba(250, 204, 21, 0.55)' : 'rgba(34, 197, 94, 0.45)' }),
          stroke: new Stroke({ color: active ? '#854d0e' : '#ecfccb', width: active ? 2.5 : 1 }),
        });
      },
    });
    const map = new OlMap({
      target: host,
      layers: [bases.satellite, bases.hybrid, bases.terrain, layer],
      controls: defaultControls({ attribution: true, zoom: true, rotate: false }),
      view: new View({
        center: fromLonLat([122.5, 12.2]),
        zoom: 5.6,
        minZoom: 4.5,
        maxZoom: 16,
      }),
    });
    bases.hybrid.setVisible(false);
    bases.terrain.setVisible(false);
    map.on('singleclick', (event) => {
      const hit = map.forEachFeatureAtPixel(event.pixel, (feature) => feature, { hitTolerance: 4 });
      const id = hit?.get('id');
      if (typeof id === 'string' && visibleRef.current.has(id)) setSelectedId(id);
    });
    mapRef.current = map;
    layerRef.current = layer;
    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [file]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected) return;
    const [minLon, minLat, maxLon, maxLat] = selected.bbox;
    map.getView().fit(transformExtent([minLon, minLat, maxLon, maxLat], 'EPSG:4326', 'EPSG:3857'), {
      padding: [48, 48, 48, 48],
      maxZoom: 14,
      duration: 250,
    });
  }, [selected]);

  const paper = isDark ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3]' : 'border-[#e5e7eb] bg-white text-[#111827]';
  const muted = isDark ? 'text-[#8b949e]' : 'text-[#6b7280]';
  const field = `w-full rounded-lg border px-2.5 py-1.5 text-sm outline-none focus:border-[#008540] ${
    isDark ? 'border-[#2d3748] bg-[#0d1117]' : 'border-[#e5e7eb] bg-white'
  }`;

  const toggleShortlist = (id: string) => {
    setShortlist((current) => {
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      localStorage.setItem(SHORTLIST_KEY, JSON.stringify(next));
      return next;
    });
  };

  const saveProject = async (candidate: Candidate) => {
    setSaving(true);
    setSaveMessage('');
    try {
      const project = await createCarbonProject({
        title: `${candidate.municipality || candidate.id} · ${candidate.form_id || candidate.id}`.slice(0, 200),
        pathway: 'restoration_agroforestry',
        location: [candidate.municipality, candidate.region].filter(Boolean).join(', '),
        objectives: `Draft from candidate ${candidate.id}. Source FORM_ID ${candidate.form_id || 'not recorded'}. Investment type: ${candidate.investment_type || 'not recorded'}. The map ring is a display simplification and is not the saved project boundary.`,
      });
      setSaveMessage(`Saved draft project “${project.title}”. The candidate polygon is not the project boundary yet.`);
    } catch (error) {
      setSaveMessage(apiErrorMessage(error, 'Could not save a project from this candidate.'));
    } finally {
      setSaving(false);
    }
  };

  const shown = filtered.slice(0, 80);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className={`border-b px-4 py-3 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#008540]">Explore · Screen · Validate</p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">SFLMA decision explorer</h2>
            <p className={`text-sm ${muted}`}>Find the landscapes worth a closer look.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setRulesOpen(true)} className={`rounded-lg border px-3 py-1.5 text-sm ${paper}`}>
              Scoring & rules
            </button>
            <button type="button" onClick={() => setLayersOpen(true)} className={`rounded-lg border px-3 py-1.5 text-sm ${paper}`}>
              Data & layers
            </button>
          </div>
        </div>
        <p className={`mt-2 rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-emerald-950/40 text-emerald-100' : 'bg-emerald-50 text-emerald-950'}`}>
          {file ? `${file.count.toLocaleString()} nationwide PIA polygons.` : 'Loading national PIA layer…'}{' '}
          R v4 indicators are not calculated in this view. Land availability, rights, crop suitability, and carbon eligibility still require validation.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {QUICK_REGIONS.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => setRegion(item.id)}
              className={`rounded-full px-3 py-1 text-sm ${
                region === item.id ? 'bg-[#008540] text-white' : isDark ? 'bg-white/10' : 'bg-[#f3f4f6]'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[300px_minmax(0,1fr)_320px]">
        <section className={`min-h-0 overflow-y-auto border-r p-3 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-semibold">Discover candidates</h3>
            <button
              type="button"
              className="text-sm font-medium text-[#008540]"
              onClick={() => {
                setQuery('');
                setRegion('');
                setInvestment('');
                setMinArea('');
                setMaxArea('');
                setShortlistOnly(false);
              }}
            >
              Reset
            </button>
          </div>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search a place or candidate…"
            className={field}
          />
          <label className="mt-3 block text-xs font-medium">
            Region
            <select value={region} onChange={(event) => setRegion(event.target.value)} className={`${field} mt-1`}>
              <option value="">All regions</option>
              {regions.map(([name, count]) => (
                <option key={name} value={name}>
                  {name} ({count.toLocaleString()})
                </option>
              ))}
            </select>
          </label>
          <label className="mt-3 block text-xs font-medium">
            Province
            <select disabled className={`${field} mt-1 opacity-70`} value="">
              <option>Not in the source file</option>
            </select>
          </label>
          <p className={`mt-1 text-xs ${muted}`}>PENRO is a numeric code in the shapefile. It is not a province name.</p>
          <label className="mt-3 block text-xs font-medium">
            Investment type
            <select value={investment} onChange={(event) => setInvestment(event.target.value)} className={`${field} mt-1`}>
              <option value="">All categories</option>
              {investmentOptions.map((option) => (
                <option key={option.key || 'blank'} value={option.key}>
                  {option.label} ({option.count.toLocaleString()})
                </option>
              ))}
            </select>
          </label>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="text-xs font-medium">
              Minimum area (ha)
              <input value={minArea} onChange={(event) => setMinArea(event.target.value)} inputMode="decimal" className={`${field} mt-1`} />
            </label>
            <label className="text-xs font-medium">
              Maximum area (ha)
              <input value={maxArea} onChange={(event) => setMaxArea(event.target.value)} placeholder="No limit" className={`${field} mt-1`} />
            </label>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={shortlistOnly} onChange={(event) => setShortlistOnly(event.target.checked)} />
            Shortlist only
            <span className={muted}>{shortlist.length.toLocaleString()} saved in this browser</span>
          </label>
          <p className="mt-3 text-sm font-semibold">{filtered.length.toLocaleString()} candidates</p>
          <ul className="mt-2 space-y-2">
            {shown.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(candidate.id)}
                  className={`w-full rounded-xl border px-3 py-2 text-left ${
                    selectedId === candidate.id ? 'border-[#008540]' : ''
                  } ${paper}`}
                >
                  <span className="block font-medium">{candidate.municipality || 'Municipality not recorded'}</span>
                  <span className={`block text-xs ${muted}`}>
                    {candidate.id} · {candidate.form_id || 'No FORM_ID'} · {candidate.region || 'Region unknown'}
                  </span>
                  <span className="mt-1 block text-sm">{formatHa(candidate.area_ha)} · Not scored</span>
                </button>
              </li>
            ))}
          </ul>
          {filtered.length > shown.length && (
            <p className={`mt-2 text-xs ${muted}`}>
              Showing {shown.length} of {filtered.length.toLocaleString()}. Search or filter to narrow the list. The map still draws every match.
            </p>
          )}
          {loadError && <p className="mt-3 text-sm text-red-600">{loadError}</p>}
        </section>

        <section className="relative min-h-[320px] min-w-0">
          <div className={`absolute left-3 right-3 top-3 z-10 grid grid-cols-3 gap-2 rounded-xl border p-2 text-center shadow-none ${paper}`}>
            <Stat label="In this view" value={file ? filtered.length.toLocaleString() : '—'} hint="candidates" muted={muted} />
            <Stat label="Gross area" value={file ? Math.round(grossArea).toLocaleString() : '—'} hint="ha · source AREA field" muted={muted} />
            <Stat label="High terrain risk" value="Unknown" hint="slope was not sampled" muted={muted} />
          </div>
          <div ref={mapHost} className="h-full w-full" />
          <p className={`pointer-events-none absolute bottom-3 left-3 z-10 max-w-md rounded-lg border px-3 py-2 text-xs ${paper}`}>
            Display rings are simplified. Source geometry stays in the UTM zone 51N shapefile. Screening score is not drawn because road distance, town distance, slope, and BIO15 were not run.
          </p>
        </section>

        <aside className={`min-h-0 overflow-y-auto border-l p-4 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
          {!selected && <p className={`text-sm ${muted}`}>Select a candidate on the map or in the list.</p>}
          {selected && (
            <div className="space-y-4">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>{selected.id} · preloaded</p>
                <h3 className="text-2xl font-semibold">{selected.municipality || 'Municipality not recorded'}</h3>
                <p className={`text-sm ${muted}`}>{[selected.region, selected.cenro && `CENRO ${selected.cenro}`].filter(Boolean).join(' · ')}</p>
                <p className="mt-2 text-sm">{selected.investment_type || 'Investment type not recorded'}</p>
                <p className={`text-xs ${muted}`}>
                  Source FORM_ID {selected.form_id || 'not recorded'}
                  {selected.investment_class ? ` · ${selected.investment_class}` : ''}
                  {selected.ppf ? ` · ${selected.ppf}` : ''}
                </p>
              </div>
              <div className={`rounded-xl border p-3 ${paper}`}>
                <p className="text-sm font-semibold">Not scored</p>
                <p className={`mt-1 text-sm ${muted}`}>
                  R v4 would weight scale 25%, road 25%, town proximity 15%, terrain 20%, and climate seasonality 15%. Those auxiliary layers are not attached, so this candidate has no feasibility number.
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Indicator label="Gross candidate area" value={formatHa(selected.area_ha)} hint="Shapefile AREA field" muted={muted} />
                <Indicator label="Nearest main road" value="Unknown" hint="OSM distance not run" muted={muted} />
                <Indicator label="PENRO code" value={selected.penro == null ? 'Unknown' : String(selected.penro)} hint="Not a province name" muted={muted} />
                <Indicator label="PSGC" value={selected.psgc || 'Not recorded'} hint="Source attribute" muted={muted} />
                <Indicator label="Point slope" value="Unknown" hint="Not a project-wide terrain result" muted={muted} />
                <Indicator label="BIO15 seasonality" value="Unknown" hint="Historical rainfall seasonality, not drought probability" muted={muted} />
              </dl>
              <div className={`rounded-xl border p-3 text-sm ${isDark ? 'border-amber-900/60 bg-amber-950/30' : 'border-amber-200 bg-amber-50'}`}>
                <p className="font-semibold">Eligibility remains unresolved</p>
                <p className="mt-1">
                  A PIA designation and a screening score do not establish land availability, consent, carbon eligibility, or an investment approval.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => toggleShortlist(selected.id)}
                  className="rounded-lg border border-[#008540] px-3 py-2 text-sm font-semibold text-[#008540]"
                >
                  {shortlist.includes(selected.id) ? 'Remove shortlist' : 'Shortlist'}
                </button>
                {publicPreview ? (
                  <Link
                    to="/userdashboard"
                    className="rounded-lg bg-[#008540] px-3 py-2 text-sm font-semibold text-white"
                  >
                    Log in to save a project
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void saveProject(selected)}
                    className="rounded-lg bg-[#008540] px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {saving ? 'Saving…' : 'Save as project'}
                  </button>
                )}
              </div>
              {saveMessage && <p className="text-sm">{saveMessage}</p>}
              <p className={`text-xs ${muted}`}>
                {publicPreview
                  ? 'Shortlist stays in this browser. Saving a private draft needs an account. This preview does not copy the polygon into a project.'
                  : 'Shortlist and notes stay in this browser. Saving a project creates a private draft. It does not copy the authoritative polygon into the project record.'}
              </p>
            </div>
          )}
        </aside>
      </div>

      {rulesOpen && (
        <Modal title="R v4 rules and transparent weights" onClose={() => setRulesOpen(false)} paper={paper}>
          <p className={`text-sm ${muted}`}>
            The supplied R script is the default model. These weights are not being applied to the polygons on the map, because the road, town, slope, and BIO15 inputs were not calculated.
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            <li>Scale / gross land — 25%</li>
            <li>Road accessibility — 25%</li>
            <li>Town proximity — 15%</li>
            <li>Terrain suitability — 20%</li>
            <li>Climate seasonality — 15%</li>
          </ul>
          <p className="mt-3 text-sm">
            Classes, if a score exists: 80 or more Very strong, 65 Strong, 50 Moderate, 35 Weak, below 35 Very weak. Missing inputs are reweighted. A missing score stays “Not scored”, not zero.
          </p>
        </Modal>
      )}
      {layersOpen && (
        <Modal title="Available layers and data dependencies" onClose={() => setLayersOpen(false)} paper={paper}>
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <LayerCard title="PIA polygons" body="8,117 records from NAL_PIA_Clean_within_PPF. Display geometry is simplified from UTM zone 51N." />
            <LayerCard title="Roads, towns, slope, BIO15" body="Used by the R v4 score. Not loaded here, so distance and hazard fields stay unknown." />
            <LayerCard title="Agroforestry / AANR" body="Crop fit and restoration design are separate from the gross PIA area and from a single terrain score." />
            <LayerCard title="Low-carbon rice" body="Needs an irrigation envelope, participating farms, and seasons. A PIA polygon is not an irrigation system." />
            <LayerCard title="Carbon eligibility" body="Rights, baseline, additionality, leakage, permanence, and verification are unresolved. No credit quantity is calculated." />
          </div>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value, hint, muted }: { label: string; value: string; hint: string; muted: string }) {
  return (
    <div>
      <p className={`text-[10px] font-semibold uppercase tracking-wide ${muted}`}>{label}</p>
      <p className="text-lg font-semibold leading-tight">{value}</p>
      <p className={`text-[11px] ${muted}`}>{hint}</p>
    </div>
  );
}

function Indicator({ label, value, hint, muted }: { label: string; value: string; hint: string; muted: string }) {
  return (
    <div>
      <dt className={`text-xs ${muted}`}>{label}</dt>
      <dd className="font-medium">{value}</dd>
      <dd className={`text-[11px] ${muted}`}>{hint}</dd>
    </div>
  );
}

function LayerCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-[#e5e7eb] p-3 dark:border-[#2d3748]">
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{body}</p>
    </div>
  );
}

function Modal({
  title,
  onClose,
  paper,
  children,
}: {
  title: string;
  onClose: () => void;
  paper: string;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-black/40 p-4">
      <div className={`mt-10 max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-2xl border p-5 ${paper}`}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="text-sm font-medium text-[#008540]" aria-label="Close">
            Close
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
